import { useTranslation } from "react-i18next";
import { Send, AlertCircle, Loader2 } from "lucide-react";
import { getPublicationStatus } from "@/lib/publicationHelpers";
import { isPublishable, PUBLISH_GATE_LABEL_KEYS, type PublishGateInput } from "@/lib/publishGate";
import { cn } from "@/lib/utils";
import type { Bien } from "@/types/bien";

/**
 * Section "Publication" simplifiée — 2 primitives exportées indépendamment
 * pour pouvoir les placer dans des cellules de grid distinctes :
 *   - PublicationPublishButton : juste le bouton "Publier" (compact)
 *   - PublicationStaleBanner   : juste la bannière "Modifs non poussées"
 *                                (rend null si pas stale)
 *
 * Décisions produit (user feedback) :
 *   - Plus de bouton "Dépublier" : redondant avec le dropdown statutPublication
 *     qui propose déjà "Retiré".
 *   - Plus de pastille statut : redondante avec la bannière + dropdown.
 *   - Bouton à la même hauteur que le dropdown (placé dans le côté droit
 *     du grid par l'appelant), bannière en col-span-2 en-dessous.
 */

export function PublicationPublishButton({
  bien,
  onPublishClick,
  isPending = false,
}: {
  bien: Bien;
  onPublishClick: () => void;
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const status = getPublicationStatus(bien);
  // Garde-fou client (publishGate) : le bouton reste grisé tant que les
  // critères de publication ne sont pas remplis, même si le statut est stale.
  const gate = isPublishable(bien as unknown as PublishGateInput);
  const canPublish = status === "stale" && gate.ok;
  const lockedReason = !gate.ok
    ? t("publishGate.blocked", {
        fields: gate.missing.map((k) => t(PUBLISH_GATE_LABEL_KEYS[k] ?? k)).join(", "),
      })
    : bien.statutPublication !== "Publié"
      ? t("portals.lockedReasonBrouillon")
      : t("portals.lockedReasonUpToDate");

  return (
    <div className="flex items-end justify-end h-full">
      <button
        type="button"
        onClick={onPublishClick}
        disabled={!canPublish || isPending}
        title={canPublish ? undefined : lockedReason}
        className={cn(
          "flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md",
          "outline-none shadow-none transition-colors",
          "text-white bg-accent hover:bg-accent/90",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          canPublish && !isPending && "cursor-pointer",
        )}
      >
        {isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        <span>{t("portals.actions.publish")}</span>
      </button>
    </div>
  );
}

export function PublicationStaleBanner({ bien }: { bien: Bien }) {
  const { t } = useTranslation();
  const status = getPublicationStatus(bien);
  if (status !== "stale") return null;
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-warning/5 border border-warning/30">
      <AlertCircle size={14} className="shrink-0 mt-0.5 text-warning" />
      <div className="text-xs leading-snug">
        <p className="font-semibold text-warning">{t("portals.staleBannerTitle")}</p>
        <p className="text-text-secondary mt-0.5">{t("portals.staleBannerBody")}</p>
      </div>
    </div>
  );
}
